import React, { useState, useRef, useEffect } from 'react';
import { documentsApi, dealsApi } from '../services/api';
import { FileText, Check, Clock, Folder } from 'lucide-react';
import './DocumentUpload.css';
import { DualTrackDocumentPanel, OfferReadinessAlert } from './SaleLeaseback';




function DocumentUpload({ onAnalysisComplete, onUploadComplete }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({});
  const [files, setFiles] = useState([]);
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [deals, setDeals] = useState([]);
  const [selectedDeal, setSelectedDeal] = useState('');
  const [recentUploads, setRecentUploads] = useState([]);
  const [dealDocuments, setDealDocuments] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    fetchDeals();
    fetchRecentUploads();
  }, []);

  useEffect(() => {
    if (selectedDeal) {
      fetchDealDocuments(selectedDeal);
    } else {
      setDealDocuments([]);
    }
  }, [selectedDeal]);

  const fetchDeals = async () => {
    try {
      const response = await dealsApi.list();
      setDeals(response.data || []);
    } catch (error) {
      console.error('Failed to fetch deals:', error);
    }
  };

  const fetchDealDocuments = async (dealId) => {
    try {
      const response = await dealsApi.getDocuments(dealId);
      setDealDocuments(response.data?.documents || response.data || []);
    } catch (error) {
      console.error('Failed to fetch deal documents:', error);
      setDealDocuments([]);
    }
  };

  const fetchRecentUploads = async () => {
    try {
      const response = await documentsApi.list();
      const docs = response.data.documents || [];
      // Get 5 most recent
      setRecentUploads(docs.slice(0, 5));
    } catch (error) {
      console.error('Failed to fetch recent uploads:', error);
    }
  };

  const validateFile = (file) => {
    const validTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
      'text/plain',
      'text/csv',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'application/vnd.ms-powerpoint',
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp',
    ];
    if (!validTypes.includes(file.type)) {
      return 'Unsupported file type. Please upload PDF, Word, Excel, PowerPoint, CSV, TXT, or an image.';
    }
    return null;
  };

  const handleFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;

    const filesArray = Array.from(fileList);
    
    // Validate all files
    for (const file of filesArray) {
      const validationError = validateFile(file);
      if (validationError) {
        setError(`${file.name}: ${validationError}`);
        return;
      }
    }

    setFiles(filesArray);
    setUploading(true);
    setError(null);

    // Single file upload
    if (filesArray.length === 1) {
      await uploadSingleFile(filesArray[0]);
    } else {
      // Batch upload
      await uploadBatchFiles(filesArray);
    }
  };

  const uploadSingleFile = async (file) => {
    try {
      const response = await documentsApi.upload(file, (progress) => {
        setProgress({ [file.name]: progress });
      }, selectedCategory || undefined);

      const jobId = response.data.jobId || response.data.id;
      setProgress({ [jobId]: 100 });

      // Complete immediately since upload is synchronous
      setTimeout(async () => {
        // Add to deal if selected
        if (selectedDeal) {
          await addDocumentToDeal(jobId, selectedDeal);
          await fetchDealDocuments(selectedDeal);
        }

        // Refresh recent uploads
        await fetchRecentUploads();

        onUploadComplete?.();
        setUploading(false);
        setFiles([]);
        // Don't clear selectedDeal so user can upload more files to same deal
      }, 500);

    } catch (err) {
      setError(err.response?.data?.error || `Failed to upload ${file.name}`);
      setUploading(false);
    }
  };

  const uploadBatchFiles = async (filesArray) => {
    try {
      // Initialise progress at 0 for all files immediately
      const initialProgress = {};
      filesArray.forEach(f => { initialProgress[f.name] = 0; });
      setProgress(initialProgress);

      // Upload files one by one, tracking progress by filename
      const results = [];
      for (const file of filesArray) {
        try {
          const response = await documentsApi.upload(file, (pct) => {
            setProgress(prev => ({ ...prev, [file.name]: pct }));
          }, selectedCategory || undefined);
          results.push({
            jobId: response.data.jobId || response.data.id,
            status: 'uploaded',
            filename: file.name
          });
          setProgress(prev => ({ ...prev, [file.name]: 100 }));
        } catch (err) {
          results.push({
            status: 'error',
            filename: file.name,
            error: err.message
          });
        }
      }
      const successResults = results.filter(r => r.jobId && (r.status === 'uploaded' || r.status === 'processing'));
      const errorResults = results.filter(r => r.status === 'error');
      const jobIds = successResults.map(r => r.jobId);

      if (errorResults.length > 0) {
        const errorNames = errorResults.map(r => r.filename).join(', ');
        setError(`Failed to parse: ${errorNames}. Other files are still processing.`);
      }

      if (jobIds.length === 0) {
        setError('All files failed to process. Please check the file formats and try again.');
        setUploading(false);
        return;
      }

      // Mark all as immediately complete since upload is synchronous now
      setTimeout(async () => {
        // Add all documents to deal if selected
        if (selectedDeal) {
          for (const jobId of jobIds) {
            await addDocumentToDeal(jobId, selectedDeal);
          }
        }
        
        await fetchRecentUploads();
        if (selectedDeal) await fetchDealDocuments(selectedDeal);
        onUploadComplete?.();
        setUploading(false);
        setFiles([]);
      }, 500);

    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload documents');
      setUploading(false);
    }
  };

  const addDocumentToDeal = async (jobId, dealId) => {
    try {
      await dealsApi.addDocument(dealId, jobId);
    } catch (error) {
      console.error('Failed to add document to deal:', error);
    }
  };

  const handleChange = (e) => {
    handleFiles(e.target.files);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    handleFiles(e.dataTransfer.files);
  };

  const onButtonClick = () => {
    inputRef.current.click();
  };

  const getOverallProgress = () => {
    const values = Object.values(progress);
    if (values.length === 0) return 0;
    return Math.round(values.reduce((a, b) => a + b, 0) / values.length);
  };

  const selectedDealObj = deals.find(d => d.id === selectedDeal);
  const isSaleLeaseback = selectedDealObj?.deal_type_id === 'sale_leaseback_industrial';

  return (
    <div className="upload-section">
      <div className="upload-layout">
        {/* Left side - Upload area */}
        <div className="upload-main">
          <div className="deal-selector-section">
            <label className="deal-selector-label">
              <Folder size={18} />
              Add to Deal (Optional)
            </label>
            <select 
              className="deal-selector"
              value={selectedDeal}
              onChange={(e) => setSelectedDeal(e.target.value)}
              disabled={uploading}
            >
              <option value="">-- Don't add to any deal --</option>
              {deals.map(deal => (
                <option key={deal.id} value={deal.id}>{deal.name}</option>
              ))}
            </select>
            <p className="deal-hint">
              Select a deal to automatically organize uploaded documents
            </p>
          </div>

          {isSaleLeaseback && (
            <div style={{ marginTop: 16, marginBottom: 8 }}>
              <OfferReadinessAlert documents={dealDocuments} />
              <DualTrackDocumentPanel documents={dealDocuments} />
              <div className="deal-selector-section" style={{ marginTop: 16 }}>
                <label className="deal-selector-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <FileText size={16} /> Document Category
                </label>
                <select
                  className="deal-selector"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  disabled={uploading}
                >
                  <option value="">-- Auto-detect category --</option>
                  <optgroup label="Property Track">
                    <option value="alta_title">ALTA Survey / Title Commitment</option>
                    <option value="phase1_esa">Phase I Environmental Site Assessment</option>
                    <option value="pca">Property Condition Assessment</option>
                    <option value="building_specs">Building Specifications</option>
                    <option value="zoning">Zoning and Use Permits</option>
                    <option value="market_rent">Market Rent Analysis</option>
                    <option value="property_tax">Property Tax Records</option>
                    <option value="liens">Existing Liens and Encumbrances</option>
                    <option value="property_insurance">Property Insurance History</option>
                  </optgroup>
                  <optgroup label="Tenant Credit Track">
                    <option value="financial_statements">Financial Statements (3 Years)</option>
                    <option value="tax_returns">Tax Returns (Federal, 3 Years)</option>
                    <option value="t12_pl">Trailing 12-Month P&L</option>
                    <option value="pe_sponsor">PE Sponsor Information</option>
                    <option value="customer_concentration">Customer Concentration Analysis</option>
                    <option value="lease_confirmation">Lease Rate Confirmation Letter</option>
                    <option value="key_contracts">Key Contracts (Top 3-5)</option>
                    <option value="debt_schedule">Debt Schedule</option>
                    <option value="industry_context">Industry / Market Context</option>
                  </optgroup>
                </select>
                <p className="deal-hint">
                  Select a category to tag this document, or leave blank for auto-detection
                </p>
              </div>
            </div>
          )}

          <div
            className={`dropzone ${dragActive ? 'active' : ''} ${uploading ? 'uploading' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={!uploading ? onButtonClick : undefined}
          >
            <input 
              ref={inputRef}
              type="file" 
              style={{ display: 'none' }}
              onChange={handleChange}
              accept=".pdf,.docx,.doc,.xlsx,.xls,.pptx,.ppt,.csv,.txt,.jpg,.jpeg,.png,.gif,.webp"
              multiple
            />
            
            {uploading ? (
              <div className="upload-progress-container">
                <div className="upload-files-list">
                  {files.map((file, idx) => {
                    const prog = progress[file.name] ?? 0;
                    return (
                    <div key={idx} className="upload-file-item">
                      <span className="file-name">{file.name}</span>
                      <div className="file-progress-bar">
                        <div 
                          className="file-progress-fill" 
                          style={{ width: `${prog}%` }} 
                        />
                      </div>
                      <span className="file-progress-text">{prog}%</span>
                    </div>
                    );
                  })}
                </div>
                
                <div className="overall-progress">
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${getOverallProgress()}%` }} 
                    />
                  </div>
                  <span>Overall: {getOverallProgress()}% - Uploading {files.length} document{files.length > 1 ? 's' : ''}...</span>
                </div>
              </div>
            ) : (
              <div className="dropzone-content">
                <div className="upload-icon"><FileText size={40} /></div>
                <p>Drag & drop documents here, or click to select</p>
                <span className="file-types">Supports PDF, Word, Excel, PowerPoint, CSV, TXT, images</span>
              </div>
            )}
            
            {error && <div className="error">{error}</div>}
          </div>
        </div>

        {/* Right side - Recently Uploaded */}
        <div className="upload-sidebar">
          <div className="recent-uploads-box">
            <h3>Recently Uploaded</h3>
            
            {recentUploads.length === 0 ? (
              <div className="recent-empty">
                <p>No documents uploaded yet</p>
              </div>
            ) : (
              <div className="recent-list">
                {recentUploads.map((doc) => (
                  <div key={doc.jobId} className="recent-item">
                    <span className="recent-icon"><FileText size={16} /></span>
                    <div className="recent-info">
                      <span className="recent-name" title={doc.filename}>
                        {doc.filename.length > 25 
                          ? doc.filename.substring(0, 22) + '...' 
                          : doc.filename}
                      </span>
                      <span className="recent-category">
                        {doc.category 
                          ? doc.category.charAt(0).toUpperCase() + doc.category.slice(1) 
                          : 'General'}
                      </span>
                    </div>
                    <span className={`recent-status ${doc.status}`}>
                      {doc.status === 'complete' ? <Check size={14} /> : <Clock size={14} />}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default DocumentUpload;
